import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ModulesProvider } from "@/context/ModulesContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import { Toaster } from "sonner";

import Login from "@/pages/Login";
import Enroll from "@/pages/Enroll";
import Productions from "@/pages/Productions";
import Dashboard from "@/pages/Dashboard";
import CalendarPage from "@/pages/CalendarPage";
import MySchedule from "@/pages/MySchedule";
import Conflicts from "@/pages/Conflicts";
import Notes from "@/pages/Notes";
import CastNotes from "@/pages/CastNotes";
import Reports from "@/pages/Reports";
import Documents from "@/pages/Documents";
import Attendance from "@/pages/Attendance";
import Scenes from "@/pages/Scenes";
import Contacts from "@/pages/Contacts";
import Departments from "@/pages/Departments";
import People from "@/pages/People";
import Rehearsals from "@/pages/Rehearsals";
import NotesHub from "@/pages/NotesHub";
import Changelog from "@/pages/Changelog";
import EmailTemplates from "@/pages/EmailTemplates";
import Announcements from "@/pages/Announcements";
import Users from "@/pages/Users";
import Auditions from "@/pages/Auditions";
import AuditionDay from "@/pages/AuditionDay";
import AuditionSignup from "@/pages/AuditionSignup";
import AuditionResponse from "@/pages/AuditionResponse";
import ChangePassword from "@/pages/ChangePassword";
import CallSheet from "@/pages/CallSheet";
import JudgePortal from "@/pages/JudgePortal";
import { ForgotPassword, ResetPassword, LegalPage } from "@/pages/AuthPages";
import Landing from "@/pages/Landing";
import Discover from "@/pages/Discover";
import CheckinPage from "@/pages/CheckinPage";
import PublicAuditionScores from "@/pages/PublicAuditionScores";
import Script from "@/pages/Script";
import CharacterDetail from "@/pages/CharacterDetail";
import PlatformLayout from "@/pages/platform/PlatformLayout";
import PlatformOverview from "@/pages/platform/PlatformOverview";
import PlatformProductions from "@/pages/platform/PlatformProductions";
import PlatformUsers from "@/pages/platform/PlatformUsers";
import PlatformLegal from "@/pages/platform/PlatformLegal";
import PlatformShowLibrary from "@/pages/platform/PlatformShowLibrary";
import PlatformModules from "@/pages/platform/PlatformModules";
import RehearsalReports from "@/pages/RehearsalReports";
import Actionables from "@/pages/Actionables";
import Budget from "@/pages/Budget";
import FacilityHome from "@/pages/FacilityHome";
import AdminDashboard from "@/pages/AdminDashboard";
import SeasonPlanner from "@/pages/SeasonPlanner";
import Settings from "@/pages/Settings";
import Forms from "@/pages/Forms";
import FormBuilder from "@/pages/FormBuilder";
import FormFill from "@/pages/FormFill";
import TechHub from "@/pages/TechHub";
import EmailLog from "@/pages/EmailLog";
import InstallPrompt from "@/components/InstallPrompt";

function App() {
  return (
    <AuthProvider>
      <ModulesProvider>
      <ConfirmProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/login" element={<Login />} />
          <Route path="/enroll" element={<Enroll />} />
          <Route path="/audition" element={<AuditionSignup />} />
          <Route path="/audition/:code" element={<AuditionSignup />} />
          <Route path="/audition/response/:token" element={<AuditionResponse />} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
          <Route path="/judge/:token" element={<JudgePortal />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<LegalPage title="Terms of Use" kind="terms" />} />
          <Route path="/privacy" element={<LegalPage title="Privacy Policy" kind="privacy" />} />

          <Route path="/productions" element={<ProtectedRoute><Productions /></ProtectedRoute>} />
          <Route path="/call-sheet/:rehearsalId" element={<ProtectedRoute requireProduction><CallSheet /></ProtectedRoute>} />
          <Route path="/checkin/:rehearsalId" element={<ProtectedRoute><CheckinPage /></ProtectedRoute>} />
          <Route path="/audition-scores/:token" element={<PublicAuditionScores />} />

          <Route path="/platform" element={<ProtectedRoute roles={["super_admin"]}><PlatformLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/platform/overview" replace />} />
            <Route path="overview" element={<PlatformOverview />} />
            <Route path="productions" element={<PlatformProductions />} />
            <Route path="users" element={<PlatformUsers />} />
            <Route path="legal" element={<PlatformLegal />} />
            <Route path="show-library" element={<PlatformShowLibrary />} />
            <Route path="modules" element={<PlatformModules />} />
          </Route>

          <Route path="/admin/dashboard" element={<ProtectedRoute roles={["org_admin","super_admin"]}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/seasons" element={<ProtectedRoute roles={["org_admin","producer","super_admin"]}><SeasonPlanner /></ProtectedRoute>} />

          <Route element={<ProtectedRoute requireProduction><Layout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/my-schedule" element={<MySchedule />} />
            <Route path="/conflicts" element={<Conflicts />} />
            <Route path="/notes" element={<Notes />} />
            <Route path="/cast-notes" element={<CastNotes />} />
            <Route path="/reports" element={<ProtectedRoute roles={["org_admin","production_staff"]}><Reports /></ProtectedRoute>} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/attendance" element={<ProtectedRoute roles={["org_admin","production_staff"]}><Attendance /></ProtectedRoute>} />
            <Route path="/scenes" element={<Scenes />} />
            <Route path="/people" element={<People />} />
            <Route path="/rehearsals" element={<Rehearsals />} />
            <Route path="/notes-hub" element={<NotesHub />} />
            <Route path="/changelog" element={<ProtectedRoute roles={["org_admin","super_admin"]}><Changelog /></ProtectedRoute>} />
            <Route path="/email-templates" element={<ProtectedRoute roles={["org_admin","production_staff"]}><EmailTemplates /></ProtectedRoute>} />
            <Route path="/email-log" element={<ProtectedRoute roles={["org_admin","production_staff","producer","super_admin"]}><EmailLog /></ProtectedRoute>} />
            <Route path="/contacts" element={<Navigate to="/people?tab=directory" replace />} />
            <Route path="/departments" element={<Navigate to="/people?tab=departments" replace />} />
            <Route path="/attendance" element={<Navigate to="/rehearsals?tab=attendance" replace />} />
            <Route path="/rehearsal-reports" element={<Navigate to="/rehearsals?tab=reports" replace />} />
            <Route path="/actionables" element={<Navigate to="/rehearsals?tab=actionables" replace />} />
            <Route path="/notes" element={<Navigate to="/notes-hub?tab=production" replace />} />
            <Route path="/cast-notes" element={<Navigate to="/notes-hub?tab=cast" replace />} />
            <Route path="/announcements" element={<ProtectedRoute><Announcements /></ProtectedRoute>} />
            <Route path="/auditions" element={<ProtectedRoute roles={["org_admin","production_staff"]}><Auditions /></ProtectedRoute>} />
            <Route path="/audition-day" element={<ProtectedRoute roles={["org_admin","production_staff"]}><AuditionDay /></ProtectedRoute>} />
            <Route path="/script" element={<ProtectedRoute><Script /></ProtectedRoute>} />
            <Route path="/characters/:id" element={<ProtectedRoute><CharacterDetail /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute roles={["org_admin","production_staff"]}><Users /></ProtectedRoute>} />
            <Route path="/budget" element={<ProtectedRoute roles={["org_admin","producer","production_staff","super_admin"]}><Budget /></ProtectedRoute>} />
            <Route path="/facilities" element={<ProtectedRoute roles={["org_admin","production_staff","producer","support_staff","super_admin"]}><FacilityHome /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute roles={["org_admin","production_staff","super_admin"]}><Settings /></ProtectedRoute>} />
            <Route path="/forms" element={<Forms />} />
            <Route path="/forms/new" element={<ProtectedRoute roles={["org_admin","super_admin"]}><FormBuilder /></ProtectedRoute>} />
            <Route path="/forms/:id/edit" element={<ProtectedRoute roles={["org_admin","super_admin"]}><FormBuilder /></ProtectedRoute>} />
            <Route path="/forms/:id/fill" element={<FormFill />} />
            <Route path="/tech" element={<ProtectedRoute roles={["org_admin","production_staff","producer","support_staff","super_admin"]}><TechHub /></ProtectedRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <InstallPrompt />
      </BrowserRouter>
      <Toaster theme="dark" position="top-right" richColors />
      </ConfirmProvider>
      </ModulesProvider>
    </AuthProvider>
  );
}

export default App;
